import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { colors, fontFamily, radius, shadow } from './tokens';

export type SelectOption<T extends string = string> = {
  value: T;
  label: string;
  description?: string;
  disabled?: boolean;
  icon?: ReactNode;
};

type Size = 'sm' | 'md';

type Props<T extends string = string> = {
  options: SelectOption<T>[];
  value?: T;
  defaultValue?: T;
  onValueChange?: (value: T) => void;
  placeholder?: string;
  disabled?: boolean;
  size?: Size;
  invalid?: boolean;
  label?: string;
  hint?: string;
  align?: 'start' | 'end';
  triggerStyle?: CSSProperties;
  wrapperStyle?: CSSProperties;
  /** Minimum menu width; defaults to trigger width */
  minMenuWidth?: number;
};

const sizes: Record<Size, { triggerPad: string; fontSize: number; minHeight: number }> = {
  sm: { triggerPad: '5px 10px', fontSize: 12.5, minHeight: 28 },
  md: { triggerPad: '8px 12px', fontSize: 13.5, minHeight: 34 },
};

export function Select<T extends string = string>({
  options,
  value,
  defaultValue,
  onValueChange,
  placeholder = 'Select…',
  disabled,
  size = 'md',
  invalid,
  label,
  hint,
  align = 'start',
  triggerStyle,
  wrapperStyle,
  minMenuWidth,
}: Props<T>) {
  const isControlled = value !== undefined;
  const [internal, setInternal] = useState<T | undefined>(defaultValue);
  const current = (isControlled ? value : internal) as T | undefined;

  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number; width: number; placeAbove: boolean } | null>(
    null,
  );
  const [highlight, setHighlight] = useState<number>(() =>
    current ? Math.max(0, options.findIndex((o) => o.value === current)) : 0,
  );

  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const listboxId = useId();
  const instanceId = useId();

  const enabledIndexes = useMemo(
    () => options.map((o, i) => (!o.disabled ? i : -1)).filter((i) => i >= 0),
    [options],
  );

  const commit = useCallback(
    (next: T) => {
      if (!isControlled) setInternal(next);
      onValueChange?.(next);
    },
    [isControlled, onValueChange],
  );

  const selected = options.find((o) => o.value === current);

  const openMenu = useCallback(() => {
    if (disabled) return;
    setOpen(true);
    const idx = options.findIndex((o) => o.value === current);
    setHighlight(idx >= 0 && !options[idx]?.disabled ? idx : (enabledIndexes[0] ?? 0));
  }, [disabled, options, current, enabledIndexes]);

  const closeMenu = useCallback(
    (returnFocus = true) => {
      setOpen(false);
      if (returnFocus) triggerRef.current?.focus();
    },
    [],
  );

  const moveHighlight = useCallback(
    (delta: number) => {
      if (enabledIndexes.length === 0) return;
      const currentPos = enabledIndexes.indexOf(highlight);
      const nextPos =
        currentPos === -1
          ? 0
          : (currentPos + delta + enabledIndexes.length) % enabledIndexes.length;
      setHighlight(enabledIndexes[nextPos]!);
    },
    [enabledIndexes, highlight],
  );

  const reposition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const menuH = Math.min(360, options.length * 40 + 8);
    const spaceBelow = window.innerHeight - rect.bottom;
    const placeAbove = spaceBelow < menuH + 8 && rect.top > menuH + 8;
    const width = Math.max(minMenuWidth ?? rect.width, rect.width);
    const left = align === 'end' ? rect.right - width : rect.left;
    setPosition({
      top: placeAbove ? rect.top - 6 : rect.bottom + 6,
      left: Math.max(8, Math.min(left, window.innerWidth - width - 8)),
      width,
      placeAbove,
    });
  }, [align, options.length, minMenuWidth]);

  useLayoutEffect(() => {
    if (!open) return;
    reposition();
  }, [open, reposition]);

  useEffect(() => {
    if (!open) return;
    const onScroll = () => reposition();
    const onResize = () => reposition();
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (contentRef.current?.contains(t)) return;
      if (triggerRef.current?.contains(t)) return;
      closeMenu(false);
    };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    document.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open, reposition, closeMenu]);

  useEffect(() => {
    if (!open) return;
    contentRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const el = contentRef.current?.querySelector<HTMLElement>(`[data-highlighted="true"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [open, highlight]);

  const handleTriggerKey = (e: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      openMenu();
    }
  };

  const handleListKey = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      moveHighlight(1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      moveHighlight(-1);
    } else if (e.key === 'Home') {
      e.preventDefault();
      if (enabledIndexes[0] !== undefined) setHighlight(enabledIndexes[0]);
    } else if (e.key === 'End') {
      e.preventDefault();
      const last = enabledIndexes[enabledIndexes.length - 1];
      if (last !== undefined) setHighlight(last);
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      const opt = options[highlight];
      if (opt && !opt.disabled) {
        commit(opt.value);
        closeMenu();
      }
    } else if (e.key === 'Escape' || e.key === 'Tab') {
      e.preventDefault();
      closeMenu();
    }
  };

  const triggerCss: CSSProperties = {
    ...triggerBase(size, invalid, disabled, open),
    ...triggerStyle,
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, ...wrapperStyle }}>
      {label && (
        <span
          style={{
            fontVariantNumeric: 'tabular-nums',
            fontSize: 10,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: colors.ink3,
          }}
        >
          {label}
        </span>
      )}
      <button
        ref={triggerRef}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-activedescendant={open ? `${instanceId}-opt-${highlight}` : undefined}
        disabled={disabled}
        onClick={() => (open ? closeMenu() : openMenu())}
        onKeyDown={handleTriggerKey}
        style={triggerCss}
      >
        <span
          style={{
            flex: 1,
            minWidth: 0,
            textAlign: 'left',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: selected ? colors.ink : colors.ink3,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          {selected?.icon}
          {selected?.label ?? placeholder}
        </span>
        <Chevron open={open} />
      </button>
      {hint && (
        <span style={{ fontSize: 11.5, color: invalid ? colors.bad : colors.ink3 }}>{hint}</span>
      )}

      {open &&
        position &&
        createPortal(
          <div
            ref={contentRef}
            role="listbox"
            id={listboxId}
            tabIndex={-1}
            onKeyDown={handleListKey}
            style={{
              position: 'fixed',
              top: position.placeAbove ? undefined : position.top,
              bottom: position.placeAbove ? window.innerHeight - position.top : undefined,
              left: position.left,
              minWidth: position.width,
              maxHeight: 360,
              overflowY: 'auto',
              background: colors.surface,
              border: `1px solid ${colors.line2}`,
              borderRadius: radius.xs,
              boxShadow: shadow.lg,
              padding: 4,
              zIndex: 200,
              outline: 'none',
              fontFamily: fontFamily.sans,
            }}
          >
            {options.map((opt, i) => {
              const isSelected = opt.value === current;
              const isHighlighted = i === highlight && !opt.disabled;
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="option"
                  id={`${instanceId}-opt-${i}`}
                  data-highlighted={isHighlighted}
                  aria-selected={isSelected}
                  aria-disabled={opt.disabled}
                  disabled={opt.disabled}
                  onMouseEnter={() => !opt.disabled && setHighlight(i)}
                  onClick={() => {
                    if (opt.disabled) return;
                    commit(opt.value);
                    closeMenu();
                  }}
                  style={optionStyle(isSelected, isHighlighted, !!opt.disabled)}
                >
                  <span style={{ width: 16, flexShrink: 0, display: 'inline-flex', alignItems: 'center' }}>
                    {isSelected ? <Check /> : opt.icon}
                  </span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 13, color: colors.ink }}>{opt.label}</span>
                    {opt.description && (
                      <span style={{ display: 'block', fontSize: 11.5, color: colors.ink3, marginTop: 1 }}>
                        {opt.description}
                      </span>
                    )}
                  </span>
                </button>
              );
            })}
            {options.length === 0 && (
              <div style={{ padding: '10px 12px', color: colors.ink3, fontSize: 13, fontStyle: 'italic' }}>
                No options.
              </div>
            )}
          </div>,
          document.body,
        )}
    </div>
  );
}

function triggerBase(size: Size, invalid?: boolean, disabled?: boolean, open?: boolean): CSSProperties {
  const s = sizes[size];
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    width: '100%',
    minHeight: s.minHeight,
    padding: s.triggerPad,
    fontFamily: fontFamily.sans,
    fontSize: s.fontSize,
    background: disabled ? colors.surface2 : colors.surface,
    color: colors.ink,
    border: `1px solid ${invalid ? colors.bad : open ? colors.ink3 : colors.line2}`,
    borderRadius: radius.xs,
    cursor: disabled ? 'not-allowed' : 'pointer',
    outline: 'none',
    textAlign: 'left',
    opacity: disabled ? 0.55 : 1,
    transition: 'border-color 0.12s',
  };
}

function optionStyle(selected: boolean, highlighted: boolean, disabled: boolean): CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    width: '100%',
    padding: '7px 10px',
    borderRadius: radius.xs / 2,
    border: 'none',
    background: highlighted ? colors.accentSoft : 'transparent',
    color: colors.ink,
    textAlign: 'left',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    fontFamily: fontFamily.sans,
    fontSize: 13,
  };
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      width={12}
      height={12}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{
        color: colors.ink3,
        flexShrink: 0,
        transition: 'transform 0.15s',
        transform: open ? 'rotate(180deg)' : 'none',
      }}
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function Check() {
  return (
    <svg
      width={12}
      height={12}
      viewBox="0 0 16 16"
      fill="none"
      stroke={colors.accent}
      strokeWidth={2.2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 8.5l3.2 3.2L13 5" />
    </svg>
  );
}
